import { motion, useInView, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { useRef, ReactNode } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import WordRevealHeading from './WordRevealHeading';
import ParallaxPattern from './ParallaxPattern';

interface ScrollRevealSectionProps {
  eyebrow?: string;
  headline: string;
  description?: string;
  children?: ReactNode;
  ctaButton?: ReactNode;
  visual?: ReactNode;
  visualPosition?: 'right' | 'bottom' | 'background';
  className?: string;
  dark?: boolean;
  showDoodlePattern?: boolean;
}

// Apple-inspired easing
const appleEase: [number, number, number, number] = [0.22, 1, 0.36, 1];
const duration = 0.8;
const stagger = 0.1;

export default function ScrollRevealSection({
  eyebrow,
  headline,
  description,
  children,
  ctaButton,
  visual,
  visualPosition = 'right',
  className = '',
  dark = false,
  showDoodlePattern = false,
}: ScrollRevealSectionProps) {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: false, margin: '-80px' });
  const reduceMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const allowParallax = !reduceMotion && !isMobile;

  // Visual drifts slower than the page, so it reads as sitting behind the text.
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const visualY = useTransform(scrollYProgress, [0, 1], [48, -48]);


  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: stagger,
      },
    },
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 8 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: duration * 0.6, ease: appleEase } 
    },
  };

  const slideUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration, ease: appleEase } 
    },
  };

  const lineReveal = {
    hidden: { scaleX: 0, originX: 0 },
    visible: { 
      scaleX: 1, 
      transition: { duration: duration * 0.7, ease: appleEase } 
    },
  };

  return (
    <section
      ref={ref}
      className={`relative py-24 md:py-32 lg:py-40 overflow-hidden ${dark ? 'bg-foreground text-background' : 'bg-background text-foreground'} ${className}`}
    >
      {showDoodlePattern && <ParallaxPattern />}
      
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className={`grid gap-12 lg:gap-20 ${visualPosition === 'right' ? 'lg:grid-cols-2 items-center' : ''}`}>
          {/* Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className={`max-w-2xl ${visualPosition === 'bottom' ? 'mx-auto text-center' : ''}`}
          >
            {/* Eyebrow */}
            {eyebrow && (
              <motion.p
                variants={fadeIn}
                className={`text-xs md:text-sm font-medium tracking-[0.2em] uppercase mb-4 ${dark ? 'text-background/60' : 'text-muted-foreground'}`}
              >
                {eyebrow}
              </motion.p>
            )}

            {/* Headline — reveals word by word */}
            <WordRevealHeading
              text={headline}
              className="font-display text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-[1.08] mb-4"
            />

            {/* Divider Line with gradient */}
            <motion.div
              variants={lineReveal}
              className={`h-[2px] w-16 mb-6 ${visualPosition === 'bottom' ? 'mx-auto' : ''} ${dark ? 'bg-gradient-to-r from-background/40 to-transparent' : 'bg-gradient-to-r from-foreground/30 to-transparent'}`}
            />

            {/* Description */}
            {description && (
              <motion.p
                variants={slideUp}
                className={`text-base md:text-lg lg:text-xl leading-[1.6] mb-8 ${dark ? 'text-background/70' : 'text-muted-foreground'}`}
              >
                {description}
              </motion.p>
            )}

            {/* Additional Content */}
            {children && (
              <motion.div variants={slideUp}>
                {children}
              </motion.div>
            )}

            {/* CTA Button */}
            {ctaButton && (
              <motion.div variants={slideUp} className="mt-8">
                {ctaButton}
              </motion.div>
            )}
          </motion.div>

          {/* Visual with premium reveal */}
          {visual && visualPosition === 'right' && (
            <motion.div
              initial={{ opacity: 0, x: 40, scale: 0.98 }}
              animate={isInView ? { opacity: 1, x: 0, scale: 1 } : { opacity: 0, x: 40, scale: 0.98 }}
              transition={{ duration: duration * 1.3, ease: appleEase, delay: stagger * 2 }}
              style={{ y: allowParallax ? visualY : 0 }}
              className="relative"
            >
              {visual}
            </motion.div>
          )}
        </div>

        {/* Visual Below Content */}
        {visual && visualPosition === 'bottom' && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 40, scale: 0.98 }}
            transition={{ duration: duration * 1.3, ease: appleEase, delay: stagger * 3 }}
            className="mt-12 md:mt-16 lg:mt-20"
          >
            {visual}
          </motion.div>
        )}
      </div>
    </section>
  );
}
